import { StyleSheet, TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import IonIcons from "@expo/vector-icons/Ionicons"
import MaterialIcons from "@expo/vector-icons/MaterialIcons"

import { CLUSTER_BTN_ROW_HEIGHT } from "src/components/CompositeInputCluster"
import { Text } from "src/components/Ignite/Text"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

/**
 * Visual constants shared across the unauth bottom action surfaces (channel
 * library, media viewer). The pill style intentionally mirrors the
 * authenticated ButtonCluster's full-bar look so the auth and unauth flows
 * present a consistent action vocabulary.
 *
 * UnauthTalkTypePill nests inner buttons (paddingV 8, paddingH 12) inside the
 * outer pill (paddingV 10, paddingH 16). UnauthActionButton uses an explicit
 * height (CLUSTER_BTN_ROW_HEIGHT) so it exactly matches the single-line height
 * of the CompositeInputCluster when they appear side-by-side.
 */
const PILL_OUTER_VERTICAL = 10
const PILL_OUTER_HORIZONTAL = 16
const PILL_INNER_VERTICAL = 8
const PILL_INNER_HORIZONTAL = 12
const ACTION_BUTTON_RADIUS = 20

interface UnauthTalkTypePillProps {
  /** Called when either the Talk or Type button is pressed. */
  onPress: () => void
  /** Optional style override for the outer pill. */
  style?: ViewStyle
  /** Show only the Talk button (used when Type is unavailable). */
  talkOnly?: boolean
}

/**
 * Combined Talk + Type pill used at the bottom of the unauthenticated
 * channel library and media viewer. Both buttons currently fan into the
 * caller's onPress (typically opening the Login or Request Access modal).
 */
export function UnauthTalkTypePill({ onPress, style, talkOnly = false }: UnauthTalkTypePillProps) {
  const {
    themed,
    theme: { colors },
  } = useAppTheme()

  return (
    <View style={[themed($talkTypePill), style]}>
      <TouchableOpacity
        style={styles.pillButton}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel="Talk"
      >
        <IonIcons name="mic" size={20} color={colors.buttonText} />
        <Text style={[styles.pillButtonText, { color: colors.buttonText }]}>Talk</Text>
      </TouchableOpacity>
      {!talkOnly && (
        <TouchableOpacity
          style={styles.pillButton}
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel="Type"
        >
          <MaterialIcons name="keyboard" size={20} color={colors.buttonText} />
          <Text style={[styles.pillButtonText, { color: colors.buttonText }]}>Type</Text>
        </TouchableOpacity>
      )}
    </View>
  )
}

interface UnauthActionButtonProps {
  /** Material icon name to render before the label. */
  icon: keyof typeof MaterialIcons.glyphMap
  /** Visible button label. */
  label: string
  /** Tap handler. */
  onPress: () => void
  /** Accessibility label override (defaults to label). */
  accessibilityLabel?: string
}

/**
 * Single-action pill button (e.g. Login) styled to match UnauthTalkTypePill so
 * adjacent action surfaces visually align.
 */
export function UnauthActionButton({
  icon,
  label,
  onPress,
  accessibilityLabel,
}: UnauthActionButtonProps) {
  const {
    themed,
    theme: { colors },
  } = useAppTheme()

  return (
    <TouchableOpacity
      style={themed($actionButton)}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
    >
      <MaterialIcons name={icon} size={20} color={colors.buttonText} />
      <Text style={[styles.pillButtonText, { color: colors.buttonText }]}>{label}</Text>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  pillButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: PILL_INNER_VERTICAL,
    paddingHorizontal: PILL_INNER_HORIZONTAL,
    borderRadius: ACTION_BUTTON_RADIUS,
    gap: 6,
  },
  pillButtonText: {
    fontSize: 15,
    fontWeight: "500",
  } as TextStyle,
})

const $talkTypePill: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  borderRadius: 9999,
  backgroundColor: colors.buttonBackground,
  paddingVertical: PILL_OUTER_VERTICAL,
  paddingHorizontal: PILL_OUTER_HORIZONTAL,
  gap: 4,
})

const $actionButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 9999,
  backgroundColor: colors.buttonBackground,
  // Pin the height to the cluster's single-line row height so the login button
  // always aligns with the CompositeInputCluster pill in its collapsed state.
  height: CLUSTER_BTN_ROW_HEIGHT,
  paddingHorizontal: PILL_OUTER_HORIZONTAL + PILL_INNER_HORIZONTAL,
  gap: 6,
})
